(() => {
  "use strict";

  const states = [
    {
      id: "ginseng",
      index: "Ginseng · Research overview",
      title: "Research focused on intestinal barrier biology",
      count: "32",
      description:
        "Across 32 non-human publications, researchers examined ginseng materials and specific preparations using intestinal permeability, mucus-producing cells, tissue structure, microbiota, and related gastrointestinal markers.",
      animal: "26",
      cell: "6",
      themes: ["Barrier + permeability", "Mucus + tissue structure", "Microbiota context"],
    },
    {
      id: "lions-mane",
      index: "Lion’s mane · Research overview",
      title: "Research focused on gastric-mucosal biology",
      count: "12",
      description:
        "Across 12 non-human publications, researchers examined lion’s mane materials and specific preparations using gastric tissue condition, wall mucus, oxidative markers, microbiota, and related gastrointestinal measures.",
      animal: "11",
      cell: "1",
      themes: ["Gastric tissue + mucus", "Oxidative markers", "Microbiota context"],
    },
    {
      id: "sesame-oil",
      index: "Whole sesame oil · Research overview",
      title: "Research across gastric and colonic tissue models",
      count: "6",
      description:
        "Across six non-human animal publications, researchers examined whole sesame oil using gastric and colonic tissue, mucus, inflammation, bleeding, and oxidative markers. Four reported favorable directions; two were mixed or unclear.",
      animal: "6",
      cell: "0",
      themes: ["Gastric + colonic tissue", "Mucus + inflammation", "Oxidative markers"],
    },
  ];

  const focusableSelector =
    'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function initResearchSection(section) {
    if (section.dataset.researchReady === "true") return;
    section.dataset.researchReady = "true";

    const root = section.querySelector("[data-research-drawer-root]");
    const drawer = section.querySelector(".vl-research-drawer");
    const openButton = section.querySelector("[data-research-open]");
    const rowButtons = [...section.querySelectorAll("[data-open-index]")];
    const openerButtons = [openButton, ...rowButtons].filter(Boolean);
    const closeButton = section.querySelector("[data-drawer-close]");
    const backdrop = section.querySelector("[data-drawer-backdrop]");
    const tabs = [...section.querySelectorAll("[data-drawer-tab]")];
    const tabList = section.querySelector('[role="tablist"]');
    const panel = section.querySelector('[role="tabpanel"]');
    const panelIndex = section.querySelector("[data-drawer-panel-index]");
    const panelTitle = section.querySelector("[data-drawer-panel-title]");
    const panelCount = section.querySelector("[data-drawer-panel-count]");
    const panelDescription = section.querySelector("[data-drawer-panel-description]");
    const panelThemes = section.querySelector("[data-drawer-panel-themes]");
    const panelAnimal = section.querySelector("[data-drawer-panel-animal]");
    const panelCell = section.querySelector("[data-drawer-panel-cell]");
    const ingredientArt = [...section.querySelectorAll("[data-drawer-ingredient-art]")];
    const methodToggle = section.querySelector("[data-drawer-method-toggle]");
    const methodPanel = section.querySelector("[data-drawer-method-panel]");
    const methodLabel = section.querySelector("[data-drawer-method-label]");

    if (!root || !drawer || !closeButton || tabs.length !== states.length) return;

    let activeIndex = 0;
    let returnFocus = null;
    let previousOverflow = "";

    function applyState(nextIndex, moveFocus = false) {
      if (nextIndex < 0 || nextIndex >= states.length) return;
      const direction = nextIndex < activeIndex ? -1 : 1;
      const state = states[nextIndex];
      activeIndex = nextIndex;

      tabList.style.setProperty("--vl-tab-index", String(nextIndex));
      tabs.forEach((tab, index) => {
        const selected = index === nextIndex;
        tab.setAttribute("aria-selected", String(selected));
        tab.tabIndex = selected ? 0 : -1;
        if (selected && moveFocus) tab.focus({ preventScroll: true });
      });

      panel.setAttribute("aria-labelledby", tabs[nextIndex].id);
      panel.style.setProperty("--vl-panel-origin", `${direction * 22}px`);
      panelIndex.textContent = state.index;
      panelTitle.textContent = state.title;
      panelCount.textContent = state.count;
      panelDescription.textContent = state.description;
      panelThemes.replaceChildren(
        ...state.themes.map((theme) => {
          const item = document.createElement("li");
          item.textContent = theme;
          return item;
        }),
      );
      panelAnimal.textContent = state.animal;
      panelCell.textContent = state.cell;
      ingredientArt.forEach((art) => {
        art.classList.toggle("is-active", art.dataset.drawerIngredientArt === state.id);
      });

      panel.classList.remove("is-switching");
      requestAnimationFrame(() => requestAnimationFrame(() => panel.classList.add("is-switching")));
    }

    function openDrawer(index = 0, trigger = openButton) {
      returnFocus = trigger;
      applyState(index);
      section.classList.add("is-drawer-open");
      root.classList.add("is-open");
      root.setAttribute("aria-hidden", "false");
      openerButtons.forEach((button) => button.setAttribute("aria-expanded", String(button === trigger)));
      previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = "hidden";
      requestAnimationFrame(() => closeButton.focus({ preventScroll: true }));
    }

    function closeDrawer() {
      if (!root.classList.contains("is-open")) return;
      root.classList.remove("is-open");
      section.classList.remove("is-drawer-open");
      root.setAttribute("aria-hidden", "true");
      openerButtons.forEach((button) => button.setAttribute("aria-expanded", "false"));
      document.documentElement.style.overflow = previousOverflow;
      const target = returnFocus;
      returnFocus = null;
      if (target?.isConnected) target.focus({ preventScroll: true });
    }

    openButton?.addEventListener("click", () => openDrawer(0, openButton));
    rowButtons.forEach((button) => {
      button.addEventListener("click", () => openDrawer(Number(button.dataset.openIndex), button));
    });
    closeButton.addEventListener("click", closeDrawer);
    backdrop.addEventListener("click", closeDrawer);

    tabs.forEach((tab, index) => {
      tab.addEventListener("click", () => applyState(index));
      tab.addEventListener("keydown", (event) => {
        let nextIndex = index;
        if (event.key === "ArrowRight" || event.key === "ArrowDown") nextIndex = (index + 1) % tabs.length;
        if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
          nextIndex = (index - 1 + tabs.length) % tabs.length;
        }
        if (event.key === "Home") nextIndex = 0;
        if (event.key === "End") nextIndex = tabs.length - 1;
        if (nextIndex === index) return;
        event.preventDefault();
        applyState(nextIndex, true);
      });
    });

    methodToggle.addEventListener("click", () => {
      const isOpen = methodToggle.getAttribute("aria-expanded") !== "true";
      methodToggle.setAttribute("aria-expanded", String(isOpen));
      methodPanel.setAttribute("aria-hidden", String(!isOpen));
      methodPanel.classList.toggle("is-open", isOpen);
      methodLabel.textContent = isOpen ? "Hide catalog criteria" : "How this catalog works";
    });

    document.addEventListener("keydown", (event) => {
      if (!root.classList.contains("is-open")) return;
      if (event.key === "Escape") {
        event.preventDefault();
        closeDrawer();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = [...drawer.querySelectorAll(focusableSelector)].filter(
        (element) => !element.hidden && element.getAttribute("aria-hidden") !== "true",
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    section.addEventListener("shopify:section:unload", closeDrawer);
  }

  function initAll(container = document) {
    container.querySelectorAll("[data-vl-pdp-research]").forEach(initResearchSection);
  }

  initAll();
  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
})();
